import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import {
    IBaselineBurstResponse,
    ISpectrumChartResponse,
    IUnitResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { BurstDataType } from 'pages/workbench/types';
import { mapBurstDataToPoints } from 'pages/workbench/workbench_tabs/trend/frequency_domain/shared';
import { AxisChartSeriesData } from 'shared/chart/core/types';
import { queryClient } from 'utils/query';

const queryKey = 'frequency-domain-burst-data';

export function invalidateFrequencyDomainData() {
    queryClient.invalidateQueries({
        queryKey: [queryKey],
    });
}

type UseFrequencyDomainDataDataOptions = {
    baseline: IBaselineBurstResponse | undefined;
    dataType: BurstDataType;

    // When using data platform data, waveform and units are loaded in TrendTab
    useDataPlatformData?: boolean;
    waveform?: DataPlatformApi.WaveformResponse;
    units?: IUnitResponse[];
};

export type SeriesData = Pick<
    AxisChartSeriesData,
    'dataPoints' | 'seriesMaxY' | 'seriesMinY' | 'seriesUnit' | 'seriesId'
>;

export type FrequencyDomainData = Readonly<{
    frequencySeriesData: SeriesData;
    baselineSeriesData: SeriesData | undefined;
    isVibrationType: boolean;
}>;

type MapSeriesInfo = Readonly<{
    data: number[];
    rate: number;
    units: string | undefined;
    seriesId: string;
}>;

function mapSeries({ data, rate, units, seriesId }: MapSeriesInfo): SeriesData {
    const dataPoints = mapBurstDataToPoints(data, rate);
    const mappedYs = dataPoints.map(({ y }) => y);

    return {
        seriesId,
        seriesUnit: units,
        seriesMinY: Math.min(...mappedYs),
        seriesMaxY: Math.max(...mappedYs),
        dataPoints,
    };
}

function mapFrequencyData(
    data: ISpectrumChartResponse,
    dataType: BurstDataType,
    baseline: IBaselineBurstResponse | undefined,
    indicatorId: string
): FrequencyDomainData {
    const burstData = data.BurstData[0];
    const frequencySeriesData = mapSeries({
        data: burstData.Data,
        rate: burstData.Rate,
        units: burstData.Units,
        seriesId: indicatorId,
    });

    const baselineData =
        dataType === 'acceleration'
            ? baseline?.BaselineBurstData.AccelFrequency
            : baseline?.BaselineBurstData.VelocityFrequency;

    const baselineSeriesData = baselineData
        ? mapSeries({
              data: baselineData.Data,
              rate: baselineData.Rate,
              units: baselineData.Units,
              seriesId: `${indicatorId}-baseline`,
          })
        : undefined;

    const isVibrationType = data.IsVibrationTypeIndicator;

    return { frequencySeriesData, baselineSeriesData, isVibrationType };
}

function mapDataPlatformData(
    waveform: DataPlatformApi.WaveformResponse | undefined,
    baseline: IBaselineBurstResponse | undefined,
    dataType: BurstDataType,
    units: IUnitResponse[] | undefined,
    indicatorId: string
): FrequencyDomainData | undefined {
    if (!waveform) {
        return undefined;
    }

    // If there's only one data series, use it. Otherwise, find the one that matches the requested data type.
    // This handles the case where a burst may not be in acceleration or velocity (e.g. pressure bursts).
    const data =
        waveform.data.length === 1
            ? waveform.data[0]
            : waveform.data.find((series) => series.dataType === dataType);

    if (!data) {
        throw new Error(`Failed to find waveform data of type ${dataType}`);
    }

    const mappedUnits =
        units?.find((unit) => unit.Code === data.units)?.Abbreviation ?? data.units;

    const frequencySeriesData = mapSeries({
        data: data.fft,
        rate: waveform.sampleRate,
        units: mappedUnits,
        seriesId: indicatorId,
    });
    const baselineData =
        dataType === 'acceleration'
            ? baseline?.BaselineBurstData.AccelFrequency
            : baseline?.BaselineBurstData.VelocityFrequency;

    const baselineSeriesData = baselineData
        ? mapSeries({
              data: baselineData.Data,
              rate: baselineData.Rate,
              units: baselineData.Units,
              seriesId: `${indicatorId}-baseline`,
          })
        : undefined;

    const isVibrationType =
        data.dataType === 'acceleration' || data.dataType === 'velocity';

    return {
        frequencySeriesData,
        baselineSeriesData,
        isVibrationType,
    };
}

export function useFrequencyDomainData(
    indicatorId: string,
    time: Date,
    options: UseFrequencyDomainDataDataOptions,
    overrides?: Omit<
        UseQueryOptions<FrequencyDomainData | undefined>,
        'queryKey' | 'queryFn'
    >
) {
    return useQuery({
        queryKey: [queryKey, indicatorId, time, options],

        queryFn: async () => {
            if (options.useDataPlatformData) {
                return mapDataPlatformData(
                    options.waveform,
                    options.baseline,
                    options.dataType,
                    options.units,
                    indicatorId
                );
            }

            const burstData = await sdClient.getBurstData({
                indicatorId,
                includeBaseline: false,
                returnTimeDomain: false,
                returnVelocity: options.dataType === 'velocity',
                dateTimestamp: time,
            });

            return mapFrequencyData(
                burstData,
                options.dataType,
                options.baseline,
                indicatorId
            );
        },

        ...overrides,
        enabled:
            (overrides?.enabled ?? true) &&
            (!options.useDataPlatformData || options.waveform !== undefined),
    });
}
